import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/apiClient";
import { formatDate } from "@/lib/status";
import { toast } from "sonner";
import { ClipboardCheck, ChevronDown, ChevronUp, CheckCircle2, XCircle, MinusCircle, CalendarClock } from "lucide-react";

const STATUS_META = {
  conforme: { label: "Conforme", icon: CheckCircle2, cls: "text-green-600" },
  nao_conforme: { label: "Não conforme", icon: XCircle, cls: "text-red-600" },
  na: { label: "N/A", icon: MinusCircle, cls: "text-slate-400" },
};

function ReportItem({ report }) {
  const [open, setOpen] = useState(false);
  const nc = report.items?.filter((i) => i.status === "nao_conforme").length || 0;
  return (
    <div className="rounded-lg border border-slate-200" data-testid={`pmoc-report-${report.id}`}>
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-3 text-left" data-testid={`pmoc-report-toggle-${report.id}`}>
        <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
          <ClipboardCheck className="h-4 w-4 text-[#1e7be0]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-800">Atendimento em {report.service_date || formatDate(report.created_at)}</p>
          <p className="text-xs text-slate-500">Técnico: {report.technician_name}</p>
        </div>
        {nc > 0 ? <Badge className="bg-red-100 text-red-700 border-red-200">{nc} NC</Badge>
          : <Badge className="bg-green-100 text-green-700 border-green-200">OK</Badge>}
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && (
        <div className="border-t border-slate-100 p-3 space-y-1.5">
          {report.next_maintenance_date && (
            <p className="text-xs text-[#0a2a6c] flex items-center gap-1 mb-2">
              <CalendarClock className="h-3.5 w-3.5" /> Próxima manutenção: {report.next_maintenance_date}
            </p>
          )}
          {report.items?.map((i) => {
            const m = STATUS_META[i.status] || STATUS_META.na;
            const Icon = m.icon;
            return (
              <div key={i.key} className="flex items-start gap-2 text-sm">
                <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${m.cls}`} />
                <div className="min-w-0">
                  <span className="text-slate-700">{i.label}</span>
                  {i.note && <p className="text-xs text-red-600">{i.note}</p>}
                </div>
              </div>
            );
          })}
          {report.general_notes && (
            <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-100">
              <b>Observações:</b> {report.general_notes}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function PmocHistoryDialog({ equipmentId, equipmentLabel, open, onOpenChange }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !equipmentId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/equipment/${equipmentId}/pmoc`);
        setReports(data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, equipmentId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="pmoc-history-dialog">
        <DialogHeader>
          <DialogTitle>Histórico PMOC</DialogTitle>
          <DialogDescription>{equipmentLabel}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <p className="text-slate-400 text-sm py-6 text-center">Carregando...</p>
        ) : reports.length === 0 ? (
          <p className="text-slate-500 py-8 text-center" data-testid="pmoc-empty">Nenhum PMOC registrado para este equipamento.</p>
        ) : (
          <div className="space-y-2" data-testid="pmoc-reports-list">
            {reports.map((r) => <ReportItem key={r.id} report={r} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
